import { useMemo } from 'react';
import { NavLink } from 'react-router-dom';
import {
  AccountingJobStateEnum,
  AccountingJobTypeEnum,
  AccountingJobTypes,
} from './types';
import formatDate from '../../helpers/format-date';
import getProgress from './get-progress';
import IconAttention from '../icons/attention';

import cn from './styles.module.css';

export default function AccountingJob({
  accountingJobsIsLoading,
  accountingJob,
  jobsLength,
  query,
  withoutMargin,
  accountingJobs,
}: AccountingJobTypes) {
  const updatedData = useMemo(() => {
    let type = '';
    let fullCurrentDate: string | React.ReactElement = '';
    let progressValue: string | React.ReactElement = '-';

    if (accountingJob) {
      const {
        progress,
        balanceLogsProgress,
        balanceSheetProgress,
        revaluationProgress,
      } = getProgress(accountingJob);

      if (accountingJob?.type === AccountingJobTypeEnum.BALANCES_LOGS) {
        type = 'Closing balances / Reports / Revaluations';
        fullCurrentDate = (
          <>
            <div className={cn.accountingJobDate}>
              {accountingJob.balancesLogsCurrentDate ? (
                <>
                  {formatDate(
                    accountingJob?.balancesLogsCurrentDate.split('T')[0],
                  )}{' '}
                  - Closing balances
                </>
              ) : (
                ''
              )}
            </div>
            <div className={cn.accountingJobDate}>
              {accountingJob.balanceSheetCurrentDate ? (
                <>
                  {formatDate(
                    accountingJob?.balanceSheetCurrentDate.split('T')[0],
                  )}{' '}
                  - Reports
                </>
              ) : (
                ''
              )}
            </div>
            <div className={cn.accountingJobDate}>
              {accountingJob.revaluationCurrentDate ? (
                <>
                  {formatDate(
                    accountingJob?.revaluationCurrentDate.split('T')[0],
                  )}{' '}
                  - Revaluations
                </>
              ) : (
                ''
              )}
            </div>
          </>
        );
      } else if (accountingJob?.type === AccountingJobTypeEnum.BALANCE_SHEET) {
        type = 'Reports';
        fullCurrentDate = accountingJob?.balanceSheetCurrentDate
          ? formatDate(accountingJob.balanceSheetCurrentDate.split('T')[0])
          : '-';
      } else if (
        accountingJob?.type === AccountingJobTypeEnum.POST_PROCESSING
      ) {
        type =
          'Recalculation of transaction amounts in the base currency /  Creation of missing statement items';
        fullCurrentDate = accountingJob?.postProcessingCurrentDate
          ? formatDate(accountingJob.postProcessingCurrentDate.split('T')[0])
          : '-';
      } else if (accountingJob?.type === AccountingJobTypeEnum.REVALUATION) {
        type = 'Revaluations';
        fullCurrentDate = accountingJob?.revaluationCurrentDate
          ? formatDate(accountingJob.revaluationCurrentDate.split('T')[0])
          : '-';
      }

      progressValue = (
        <div className={cn.progressWrapper}>
          <div className={cn.progressWrapperTop}>
            <div
              className={`${cn.progressWrapperTopValue} ${cn.withMargin}`}
            >{`Processed ${progress}%`}</div>
            {accountingJob?.state === AccountingJobStateEnum.IN_PROGRESS && (
              <div className={cn.progressWrapperTopValue}>
                {`up to ${
                  accountingJob?.type !== AccountingJobTypeEnum.BALANCES_LOGS
                    ? fullCurrentDate
                    : formatDate(
                        accountingJob?.balancesLogsCurrentDate?.split(
                          'T',
                        )[0] as string,
                      )
                }`}
                {accountingJob?.type ===
                  AccountingJobTypeEnum.BALANCES_LOGS && (
                  <div className={cn.accountingJobDates}>
                    +2
                    <div className={cn.accountingJobDatesHint}>
                      {fullCurrentDate}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
          <div className={cn.progressLine}>
            {accountingJob?.type === AccountingJobTypeEnum.BALANCES_LOGS &&
              accountingJob?.state === AccountingJobStateEnum.IN_PROGRESS &&
              (balanceLogsProgress?.toString() ||
                balanceSheetProgress?.toString() ||
                revaluationProgress?.toString()) && (
                <div className={cn.jobProgressTooltip}>
                  {`Closing Balances: ${balanceLogsProgress}%`}
                  <br />
                  {`Reports: ${balanceSheetProgress}%`}
                  <br />
                  {`Revaluation: ${revaluationProgress}%`}
                </div>
              )}
            <div className={cn.progressBar} style={{ width: `${progress}%` }} />
          </div>
        </div>
      );
    }

    return {
      type,
      fullCurrentDate,
      progressValue,
    };
  }, [accountingJob]);
  return (
    <>
      {accountingJobsIsLoading && (
        <div className={cn.accountingJobsIsLoading}>
          Accounting Jobs is Loading...
        </div>
      )}
      {accountingJob &&
        !accountingJobsIsLoading &&
        accountingJobs &&
        accountingJobs?.length > 0 && (
          <div
            className={`${cn.accountingJobWrapper} ${
              withoutMargin ? cn.withoutMargin : ''
            }`}
          >
            <div className={cn.accountingJobTop}>
              <div className={cn.accountingJobTopLeft}>
                <div className={cn.accountingJobTitle}>{updatedData.type}</div>
                <div className={cn.accountingJobStateWrapper}>
                  <div className={cn.accountingJobState}>
                    {accountingJob?.state
                      ?.split('_')
                      .map(
                        (item) =>
                          `${item.charAt(0)}${item.slice(1).toLowerCase()}`,
                      )
                      .join(' ')}
                  </div>
                </div>
                <div className={cn.separatorWrapper}>
                  <div className={cn.separator} />
                </div>
                <div className={cn.accountingJobAccounts}>
                  {accountingJob?.accountsIds &&
                  accountingJob?.accountsIds?.length > 0
                    ? `${accountingJob?.accountsIds?.length} account${
                        accountingJob?.accountsIds?.length > 1 ? 's' : ''
                      }`
                    : 'All accounts'}
                </div>
                <div className={cn.separatorWrapper}>
                  <div className={cn.separator} />
                </div>
                <div className={cn.accountingJobPeriodFrom}>
                  {formatDate(accountingJob.periodFrom.split('T')[0])}
                </div>
                {updatedData.progressValue}
                {accountingJob?.state !== AccountingJobStateEnum.NEW &&
                  accountingJob?.periodTo && (
                    <div className={cn.accountingJobPeriodTo}>
                      {formatDate(accountingJob.periodTo.split('T')[0])}
                    </div>
                  )}
              </div>
              <div className={cn.accountingJobTopRight}>
                <NavLink
                  to={`/reports/accounting-managment/list/${accountingJob?.id}${query}`}
                  className={cn.jobLink}
                  target="_blank"
                >
                  Details
                </NavLink>
                {jobsLength && jobsLength > 1 && (
                  <NavLink
                    to={`/reports/accounting-managment/list${query}`}
                    className={cn.jobLink}
                    target="_blank"
                  >
                    {`Show all (${jobsLength})`}
                  </NavLink>
                )}
              </div>
            </div>
            <div className={cn.accountingJobBottom}>
              <div className={cn.accountingJobBottomLeft}>
                <div className={cn.accountingJobBottomTitle}>
                  Accounting processing
                </div>
                <div
                  className={`${cn.accountingJobBottomDate} ${cn.withMargin}`}
                >{`Created: ${
                  accountingJob.createdAt
                    ? formatDate(accountingJob.createdAt, true)
                    : '-'
                }`}</div>
                <div className={cn.accountingJobBottomDate}>{`Started: ${
                  accountingJob.startedAt
                    ? formatDate(accountingJob.startedAt, true)
                    : '-'
                }`}</div>
              </div>
              {accountingJob?.state === AccountingJobStateEnum.IN_PROGRESS && (
                <div className={cn.accountingJobBottomRight}>
                  <div className={cn.accountingJobBottomIconWrapper}>
                    <IconAttention width={16} height={16} />
                  </div>
                  Attention: The data in the report may be incomplete before the
                  update process is completed.
                </div>
              )}
            </div>
          </div>
        )}
    </>
  );
}
